/**
 * K4c.2 — one tenant security, and its documents.
 *
 * WHAT THIS IS
 * ------------
 * The canonical "Details" surface for a row on /admin/kautionen. Everything it
 * SHOWS comes from the row the reader clicked — `TenantSecurityItem` already
 * carries the tenant, the tenancy, the unit, the room, the type, the amount,
 * the provider, the premium, both dates and the backend's `security_state`. So
 * opening this modal costs exactly one request, and it is for documents.
 *
 * WHAT IT IS NOT
 * --------------
 * Not an editor. The deposit's own fields belong to `DepositEditModal` and the
 * payout to `DepositPayoutModal`, both on the tenant overview, and K4x spent
 * four slices reducing this data to ONE owner each. A third entry point here
 * would undo that, so the facts are read-only and the way to change them is
 * the "Mieter öffnen" link.
 *
 * That link is also why this modal exists in the shape it does. Before K4c.2
 * the row's "Details" was a `<Link>` straight to the tenant page; turning it
 * into a modal trigger would have quietly removed middle-click, "open in new
 * tab" and copy-link. The navigation did not disappear — it moved in here, as
 * a real `<Link>`, one click further along.
 *
 * DOCUMENTS
 * ---------
 * Deposit documents live on `tenancy_documents` like every other tenancy
 * document; there is no separate store and no separate route. Three of the
 * nine categories are securities, and only those three are shown and offered.
 * `other` is excluded on purpose: offering it would let "I am not sure" become
 * a deposit document.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../components/ui/responsive-modal";
import {
  deleteAdminTenancyDocument,
  fetchAdminTenancyDocumentDownloadUrl,
  fetchAdminTenancyDocuments,
  uploadAdminTenancyDocument,
} from "../../../api/adminData";
import { chf, chfPrecise } from "../../../components/admin/companyFinance/financeFormat";
import { formatDateOnly } from "../../../utils/datetime";
import DepositBadge from "./DepositBadge";
import DepositDocumentsList from "./DepositDocumentsList";
import {
  depositSecurityState,
  depositTypeLabel,
  providerLabel,
} from "./depositsLabels";
import {
  isTenantDepositDocument,
  TENANT_DEPOSIT_DOCUMENT_CATEGORIES,
  tenantDepositDocumentCategoryLabel,
} from "./landlordDepositDocumentCategories";
import { tenancyStatusLabelDe } from "../../../utils/tenancyStatusLabels";

/** Deposits tones onto DepositBadge tones — same mapping the tables use. */
const BADGE_TONE = {
  positive: "positive",
  warn: "warn",
  neutral: "neutral",
  muted: "muted",
};

function Fact({ label, value, testId }) {
  if (value == null || value === "") return null;
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd
        data-testid={testId}
        className="break-words text-[13px] font-medium text-foreground"
      >
        {value}
      </dd>
    </div>
  );
}

export default function TenantDepositDetailModal({ item, open, onOpenChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const tenancyId = item?.tenancy_id || null;

  const load = useCallback(async () => {
    if (!tenancyId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAdminTenancyDocuments(tenancyId);
      setDocs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.message || "Dokumente konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [tenancyId]);

  /**
   * ON DEMAND, and that is load-bearing.
   *
   * /admin/kautionen lists every security an org holds. Fetching documents per
   * row would be an N+1 on a page whose whole point is a fast overview, so
   * nothing is requested until a reader opens one of them. `useDepositsData`
   * knows nothing about documents and must keep knowing nothing.
   *
   * Re-fetching on every open (rather than caching) is the deliberate choice:
   * uploads and deletes happen elsewhere too, and a stale list in a screen
   * this small is worse than one more request.
   */
  useEffect(() => {
    if (!open) {
      setDocs([]);
      setError(null);
      return;
    }
    load();
  }, [open, load]);

  const visible = docs.filter(isTenantDepositDocument);

  const handleUpload = async (file, category) => {
    if (!tenancyId) return;
    setUploading(true);
    try {
      await uploadAdminTenancyDocument(tenancyId, file, { category });
      toast.success("Dokument hochgeladen.");
      // Only this list. Documents carry no deposit KPI semantics, so the
      // aggregate the page is showing cannot have changed.
      await load();
    } catch (err) {
      toast.error(err?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const data = await fetchAdminTenancyDocumentDownloadUrl(doc.id);
      // A presigned URL from the API, never the stored object key: the bucket
      // is not public and a raw key would be both broken and a leak.
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.message || "Download konnte nicht gestartet werden.");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    setDeletingId(doc.id);
    try {
      await deleteAdminTenancyDocument(doc.id);
      toast.success("Dokument gelöscht.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!item) return null;

  const meta = item.security_state
    ? depositSecurityState(item.security_state, item.deposit_type)
    : null;
  const premium = Number(item.annual_premium_chf);
  // `null` means no payout figure was ever recorded; 0 means a payout of
  // zero, which IS a fact. `Number(null)` is 0, so the absence has to be
  // tested before the coercion — the same distinction DepositPayoutModal
  // draws in `hasDepositPayoutDetails`.
  const returned =
    item.deposit_returned_amount == null
      ? null
      : Number(item.deposit_returned_amount);
  const objectLabel = [item.unit_title, item.short_unit_id]
    .filter(Boolean)
    .join(" · ");

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Kautionsdetails – ${item.tenant_name || "Mieter"}`}
      description="Angaben aus dem Mietverhältnis. Bearbeitet wird die Kaution auf der Mieter-Übersicht."
      contentClassName="max-w-[560px]"
    >
      <div
        data-testid="tenant-deposit-detail"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1 text-foreground"
      >
        <div className="space-y-3">
          {meta ? (
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">Status</span>
              <DepositBadge
                tone={BADGE_TONE[meta.tone] || "neutral"}
                testId="tenant-deposit-detail-state"
              >
                {meta.label}
              </DepositBadge>
            </div>
          ) : null}

          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fact
              label="Mieter"
              value={item.tenant_name}
              testId="tenant-deposit-detail-tenant"
            />
            <Fact
              label="Mietverhältnis"
              value={item.reference_number}
              testId="tenant-deposit-detail-reference"
            />
            <Fact
              label="Status Mietverhältnis"
              value={
                item.tenancy_status
                  ? tenancyStatusLabelDe(item.tenancy_status)
                  : null
              }
              testId="tenant-deposit-detail-tenancy-status"
            />
            <Fact
              label="Objekt"
              value={objectLabel || null}
              testId="tenant-deposit-detail-unit"
            />
            <Fact
              label="Zimmer"
              value={item.room_name}
              testId="tenant-deposit-detail-room"
            />
            <Fact
              label="Kautionsart"
              value={depositTypeLabel(item.deposit_type)}
              testId="tenant-deposit-detail-type"
            />
            <Fact
              label="Betrag"
              value={chf(item.effective_amount_chf)}
              testId="tenant-deposit-detail-amount"
            />
            <Fact
              label="Anbieter"
              value={providerLabel(item.provider, item.deposit_type)}
              testId="tenant-deposit-detail-provider"
            />
            {/* Only for an insurance security that actually records one — a
                premium of 0 on a cash deposit is not a fact worth a row. */}
            <Fact
              label="Prämie p.a."
              value={premium > 0 ? chfPrecise(premium) : null}
              testId="tenant-deposit-detail-premium"
            />
            {/* Both rows name the COLUMN, not the state, and both are driven by
                the date column itself.

                Two reasons, and DepositCard learned them first. "Zurückbezahlt"
                is what a CASH deposit does; an insurance guarantee is
                "Freigegeben" — so a fixed "Zurückbezahlt am" here would be
                wrong for half the rows, and a near-miss spelling of the badge
                beside it reads as a second, slightly different fact.

                And whether the event happened at all is what the badge above
                says, derived by the backend from these very flags. Reading
                `deposit_received` / `deposit_returned` here to decide the same
                thing would re-run that rule client-side, which is exactly the
                disagreement `derive_tenant_security_state` exists to prevent.
                So the flags are not read in this file at all. */}
            <Fact
              label="Erhalten am"
              value={
                item.deposit_received_at
                  ? formatDateOnly(item.deposit_received_at)
                  : null
              }
              testId="tenant-deposit-detail-received-at"
            />
            <Fact
              label="Rückgabe am"
              value={
                item.deposit_returned_at
                  ? formatDateOnly(item.deposit_returned_at)
                  : null
              }
              testId="tenant-deposit-detail-returned-at"
            />
            {/* The one payout figure the row carries. Method, IBAN, account
                holder and note are NOT fetched: they belong to
                DepositPayoutModal, and a partial copy here would read as a
                second, thinner truth. */}
            <Fact
              label="Rückgabebetrag"
              value={returned != null && Number.isFinite(returned) ? chf(returned) : null}
              testId="tenant-deposit-detail-returned-amount"
            />
          </dl>

          {item.tenant_id ? (
            <Link
              to={`/admin/tenants/${item.tenant_id}`}
              data-testid="tenant-deposit-detail-link"
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Mieter öffnen
            </Link>
          ) : null}
        </div>

        <div className="border-t border-border pt-3">
          <DepositDocumentsList
            documents={visible}
            loading={loading}
            error={error}
            onRetry={load}
            categoryOptions={TENANT_DEPOSIT_DOCUMENT_CATEGORIES}
            categoryLabel={tenantDepositDocumentCategoryLabel}
            onUpload={handleUpload}
            onDownload={handleDownload}
            onDelete={handleDelete}
            uploading={uploading}
            deletingId={deletingId}
            testIdPrefix="tenant-deposit"
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
